import { useEffect, useState } from "react";

import { SupportingText } from "@core/components/SupportingText";
import classNames from "classnames";

import { UiMinusIcon } from "@jti/brand-icons/ui-minus";
import { UiPlusIcon } from "@jti/brand-icons/ui-plus";

import type { SafeHtmlAttributes } from "../../types/SafeHtmlAttributes";

import { QuantityInputItem } from "./components/QuantityInputItem";

import styles from "./QuantityInput.module.scss";

export const QUANTITY_INPUT_STATUSES = ["default", "error", "info"] as const;

const SIGNED_VALUE_PATTERN = /^-?\d*$/;

const UNSIGNED_VALUE_PATTERN = /^\d*$/;

export type QuantityInputStatus = (typeof QUANTITY_INPUT_STATUSES)[number];

export type QuantityInputAlign = "start" | "end";

export type QuantityInputProps = {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  disabledAdd?: boolean;
  disabledMinus?: boolean;
  min?: number;
  max?: number;
  status?: QuantityInputStatus;
  helperText?: string;
  /**
   * Aligns the quantity control and supporting text block within the wrapper. Text stays start aligned.
   * @deprecated Align the quantity input from the parent layout instead.
   */
  align?: QuantityInputAlign;
  increaseButtonAriaLabel?: string;
  decreaseButtonAriaLabel?: string;
  ref?: React.Ref<HTMLInputElement>;
} & Omit<SafeHtmlAttributes<HTMLInputElement>, "onChange">;

export const QuantityInput = ({
  value,
  onChange,
  disabledAdd = false,
  disabledMinus = false,
  disabled = false,
  min = 0,
  max,
  status = "default",
  helperText,
  align = "start",
  increaseButtonAriaLabel,
  decreaseButtonAriaLabel,
  ref,
  onBlur,
  onKeyDown,
  ...rest
}: QuantityInputProps) => {
  const [displayValue, setDisplayValue] = useState(String(value));

  const valuePattern = min < 0 ? SIGNED_VALUE_PATTERN : UNSIGNED_VALUE_PATTERN;

  useEffect(() => {
    setDisplayValue(String(value));
  }, [value]);

  const clamp = (newValue: number) => {
    if (min !== undefined && newValue < min) return min;
    if (max !== undefined && newValue > max) return max;
    return newValue;
  };

  const commitValue = (rawValue: string) => {
    const parsed = Number(rawValue);

    if (rawValue.trim() === "" || Number.isNaN(parsed)) {
      setDisplayValue(String(value));
      return;
    }

    const clamped = clamp(Math.round(parsed));
    setDisplayValue(String(clamped));

    if (clamped !== value) {
      onChange(clamped);
    }
  };

  const stepValue = (step: number) => {
    const parsed = Number(displayValue);
    const base = displayValue.trim() === "" || Number.isNaN(parsed) ? value : Math.round(parsed);
    const clamped = clamp(base + step);

    setDisplayValue(String(clamped));

    if (clamped !== value) {
      onChange(clamped);
    }
  };

  const updateValue = (newValue: number) => {
    const clamped = clamp(newValue);
    onChange(clamped);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;

    if (!valuePattern.test(nextValue)) return;

    setDisplayValue(nextValue);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    commitValue(displayValue);
    onBlur?.(event);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      commitValue(displayValue);
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();

      if (!disabledAdd) {
        stepValue(1);
      }
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();

      if (!disabledMinus) {
        stepValue(-1);
      }
    }

    onKeyDown?.(e);
  };

  const showSupportingText = status !== "default" && helperText;

  return (
    <div className={classNames(styles.wrapper, styles[align])}>
      <div
        className={classNames(styles.quantityInput, {
          [styles.disabled]: disabled,
        })}
      >
        <QuantityInputItem
          icon={UiMinusIcon}
          onClick={() => updateValue(value - 1)}
          aria-label={decreaseButtonAriaLabel}
          disabled={disabled || disabledMinus || value <= (min ?? 0)}
        />
        <input
          {...rest}
          ref={ref}
          type="text"
          inputMode={min < 0 ? "text" : "numeric"}
          role="spinbutton"
          aria-valuenow={value}
          aria-valuemin={min}
          aria-valuemax={max}
          className={styles.value}
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
        <QuantityInputItem
          icon={UiPlusIcon}
          onClick={() => updateValue(value + 1)}
          aria-label={increaseButtonAriaLabel}
          disabled={disabled || disabledAdd || (max !== undefined && value >= max)}
        />
      </div>
      {showSupportingText && <SupportingText variant={status}>{helperText}</SupportingText>}
    </div>
  );
};
